import { cva, type VariantProps } from "class-variance-authority";
import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from "react";
import { cn } from "../lib/cn";
import { Spinner } from "./Spinner";
import { Tooltip } from "./Tooltip";

export const iconButtonVariants = cva(
  [
    "press relative inline-flex shrink-0 select-none items-center justify-center rounded-pill",
    "disabled:pointer-events-none disabled:opacity-45 [&_svg]:shrink-0",
  ],
  {
    variants: {
      variant: {
        primary: "bg-accent text-on-accent shadow-tinted hover:bg-accent-hover",
        secondary: "bg-surface-3 text-ink shadow-control hover:bg-surface-4",
        ghost: "text-ink-2 hover:bg-fill-2 hover:text-ink",
        tinted: "bg-accent-soft text-accent-ink hover:bg-accent-soft-hover",
        contrast: "bg-contrast text-on-contrast shadow-control hover:bg-contrast-hover",
        danger: "bg-danger text-on-danger shadow-tinted hover:bg-danger-hover",
      },
      size: {
        sm: "size-7 [&_svg]:size-3.5",
        md: "size-8 [&_svg]:size-4",
        lg: "size-10 [&_svg]:size-[18px]",
      },
    },
    defaultVariants: { variant: "ghost", size: "md" },
  },
);

export interface IconButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "aria-label">,
    VariantProps<typeof iconButtonVariants> {
  /** Required. Read by screen readers and shown as the tooltip. */
  label: string;
  icon: ReactNode;
  shortcut?: string;
  /** Set false to suppress the tooltip (for example inside a labelled toolbar). */
  tooltip?: boolean;
  loading?: boolean;
}

/** Circular icon-only button. Always labelled, with a tooltip unless told otherwise. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    label,
    icon,
    shortcut,
    tooltip = true,
    loading,
    variant,
    size,
    className,
    disabled,
    type,
    ...props
  },
  ref,
) {
  const button = (
    <button
      ref={ref}
      type={type ?? "button"}
      aria-label={label}
      disabled={disabled || loading}
      className={cn(iconButtonVariants({ variant, size }), className)}
      {...props}
    >
      {loading ? <Spinner label="" /> : icon}
    </button>
  );
  if (!tooltip) return button;
  return (
    <Tooltip content={label} shortcut={shortcut}>
      {button}
    </Tooltip>
  );
});
